import { useCallback, useContext, useEffect, useRef, useState } from "react";
import * as Tone from "tone";
import ToneContext from "../../store/tone-context";
import { noteQuarterDuration } from "../../lib/book-practice-score";
import { clearPlayback } from "../../lib/tone";
import { baseOf, isPitched } from "../../lib/instruments";
import { loadPartSounds, partSound } from "../../lib/instrument-sounds";
import { practice } from "../../lib/practice/session";

// A score's notes as a preview plays them, from `start` seconds: { events: [{ time, instrument,
// pitch, duration, velocity }], parts (the instruments of its parts that play), end }.
export function previewEvents(score, tempo, start = 0) {
  const events = [], parts = new Set(), tied = new Map();
  let offset = start;
  for (const measure of score.measures) {
    for (const part of measure.parts) {
      if (score.parts?.[part.instrument]?.enabled === false) continue;
      parts.add(part.instrument);
      const key = (voice) => `${part.instrument}:${part.voices.indexOf(voice)}`;
      for (const voice of part.voices) {
        let time = offset;
        voice.notes.forEach((note, index) => {
          const duration = noteQuarterDuration(note, index, voice.tuplets || []) * 60 / tempo;
          const markings = note.ornaments || "";
          // A tied note carries on the one before it.
          const held = tied.get(key(voice));
          tied.delete(key(voice));
          if (held && note.notes.length) { held.duration += duration; if (note.tie) tied.set(key(voice), held); time += duration; return; }
          // A pitched chord sounds together for its written length.
          if (isPitched(part.instrument) && note.notes.length) {
            const chord = { time, instrument: part.instrument, pitch: [...note.notes], duration, velocity: markings.includes("a") ? 1 : .7 };
            events.push(chord);
            if (note.tie) tied.set(key(voice), chord);
          } else for (const pitch of note.notes) {
            const event = { time, instrument: part.instrument, pitch: markings.includes("b") ? "G5" : pitch, velocity: markings.includes("a") ? 1 : .5 };
            events.push(event);
            if (/[dc]/.test(markings)) events.push({ ...event, time: time + duration / 2 });
            if (/[fc]/.test(markings)) events.push({ ...event, time: time - .0175, velocity: .3 });
          }
          time += duration;
        });
      }
    }
    offset += measure.timeSig.num * 4 / measure.timeSig.type * 60 / tempo;
  }
  return { events, parts, end: offset };
}

export default function usePreviewAudio() {
  const samples = useContext(ToneContext);
  const [playing, setPlaying] = useState(null), [error, setError] = useState("");
  const run = useRef({ revision: 0, part: null, end: null });
  const stop = useCallback(() => {
    run.current.revision++;
    if (run.current.part) {
      run.current.part.dispose();
      Tone.Transport.stop();
      Tone.Transport.position = 0;
    }
    if (run.current.end != null) Tone.Transport.clear(run.current.end);
    run.current.part = null;
    run.current.end = null;
    practice.playStopped();
    setPlaying(null);
  }, []);
  useEffect(() => stop, [stop]);
  // `about` names what's playing for a practice session and the rhythm's stats: { name, source, refs }.
  async function play(key, score, tempo, about = {}) {
    const wasPlaying = playing === key;
    stop(); setError("");
    if (wasPlaying) return;
    const revision = run.current.revision;
    setPlaying(key);
    try {
      await Tone.start();
      await Tone.loaded();
      if (revision !== run.current.revision) return;
      // The piano and other instruments load the first time a previewed rhythm has them.
      const keys = score.measures.flatMap((measure) => measure.parts.map((part) => part.instrument));
      const piano = keys.some((key) => baseOf(key) === "piano") ? await samples.loadPiano() : null;
      await loadPartSounds(keys);
      if (revision !== run.current.revision) return;
      const instruments = { snare: samples.snareSampler, drumset: samples.setSampler, tenors: samples.tenorsSampler, bass: samples.bassSampler, cymbals: samples.cymbalsSampler, piano };
      const samplerOf = (instrument) => instruments[baseOf(instrument)] || partSound(instrument);
      const { events, parts, end: offset } = previewEvents(score, tempo, .04);
      if ([...parts].some((instrument) => !samplerOf(instrument)?.loaded)) throw new Error("Drum samples are still loading. Please try again.");
      clearPlayback();
      Tone.Transport.stop(); Tone.Transport.position = 0;
      run.current.part = new Tone.Part((time, note) => { practice.expect(time, note.velocity); samplerOf(note.instrument).triggerAttackRelease(note.pitch, note.duration || "32n", time, note.velocity); }, events).start(0);
      run.current.end = Tone.Transport.scheduleOnce((time) => Tone.Draw.schedule(stop, time), offset + .12);
      Tone.Transport.start();
      practice.playStarted({ name: about.name || "Rhythm preview", score, tempo, source: about.source || "preview", refs: about.refs || [] });
    } catch (failure) { if (revision === run.current.revision) { stop(); setError(failure.message); } }
  }
  return { playing, error, play, stop };
}
